import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaGlobe } from 'react-icons/fa';
import { FaArrowRight } from "react-icons/fa6";


const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState('EN');

    const languages = [
        { code: 'EN', label: 'English', flag: '🇺🇸' },
        { code: 'FR', label: 'Français', flag: '🇫🇷' },
        { code: 'AR', label: 'العربية', flag: '🇹🇳' }
    ];

    const navigationItems = [
        { label: 'Home', path: '/' },
        { 
            label: 'About us', 
            path: '/about',
            hasDropdown: true,
            dropdownItems: [
                { label: 'About We Tekup', path: '/about#about-wetekup' },
                { label: 'About We capture Studio', path: '/about#about-wecapture' },
                { label: 'About the founder', path: '/about#about-founder' },
                { label: 'About the team', path: '/about#about-team' },
                { label: 'Testimonials', path: '/about#testimonials' }
            ]
        },
        { label: 'Services', path: '/services' },
        { label: 'Portfolio', path: '/portfolio' },
        { label: 'Creative Studio', path: '/studio' },
        { label: 'Contact', path: '/contact' }
    ];

    const isActivePath = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    const handleNavigation = (path) => {
        // Handle section navigation for About page
        if (path.includes('#')) {
            const [pagePath, sectionId] = path.split('#');
            navigate(pagePath);
            // Scroll to section after navigation
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);
        } else {
            navigate(path);
        }
        setIsMobileMenuOpen(false);
        setIsAboutDropdownOpen(false);
        setIsLanguageDropdownOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
        setIsAboutDropdownOpen(false);
        setIsLanguageDropdownOpen(false);
    };

    const toggleAboutDropdown = () => {
        setIsAboutDropdownOpen(!isAboutDropdownOpen);
    };

    const toggleLanguageDropdown = () => {
        setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
    };

    const handleLanguageChange = (languageCode) => {
        setCurrentLanguage(languageCode);
        setIsLanguageDropdownOpen(false);
        // Here you can add language change logic
        console.log('Language changed to:', languageCode);
    };

    return (
        <nav className="relative top-0 left-0 right-0  backdrop-blur-[10px]  z-[1000] py-3 transition-all duration-300">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between">
                {/* Logo */}
                <div className="flex items-center gap-3 cursor-pointer transition-transform duration-300 hover:scale-105" onClick={() => handleNavigation('/')}>
                    <img src="/logowetekup.svg" alt="WeTekup" className="h-40 md:h-44 w-auto -my-20 max-md:-mx-2" />
                    {/* <span className="text-base  tracking-wider md:text-xl font-bold text-slate-800 tracking-tight"><span className='font-light'>We</span>Tekup</span> */}
                </div>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center gap-8">
                    {navigationItems.map((item) => (
                        <div key={item.label} className="relative">
                            {item.hasDropdown ? (
                                <div className="relative">
                                    <button
                                        className={`flex items-center gap-2 text-black text-[0.95rem] font-light cursor-pointer py-2 transition-all duration-300 relative  ${
                                            isActivePath(item.path) ? 'text-slate-800 font-meduil after:content-[""] after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-blue-500 after:rounded-sm' : ''
                                        }`}
                                        onClick={toggleAboutDropdown}
                                        onMouseEnter={() => setIsAboutDropdownOpen(true)}
                                    >
                                        {item.label}
                                        <FaChevronDown className={`text-xs transition-transform duration-300 ${isAboutDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <div 
                                        className={`absolute top-full left-0 bg-white border border-black/10 rounded-xl shadow-[0_10px_25px_rgba(0,0,0,0.1)] py-2 min-w-[220px] z-[1001] transition-all duration-300 ${
                                            isAboutDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2.5'
                                        }`}
                                        onMouseLeave={() => setIsAboutDropdownOpen(false)}
                                    >
                                        {item.dropdownItems.map((dropdownItem) => (
                                            <button
                                                key={dropdownItem.label}
                                                className="block w-full text-black text-sm font-light cursor-pointer py-3 px-6 text-left transition-all duration-300 hover:bg-slate-50 hover:text-blue-500"
                                                onClick={() => handleNavigation(dropdownItem.path)}
                                            >
                                                {dropdownItem.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <button
                                    className={`text-black text-[0.95rem] font-light cursor-pointer py-2 transition-all duration-300 relative hover:text-blue-500 ${
                                        isActivePath(item.path) ? 'text-slate-800 font-meduim after:content-[""] after:absolute after:-bottom-1 after:left-0 after:right-0 after:h-0.5 after:bg-blue-500 after:rounded-sm' : ''
                                    }`}
                                    onClick={() => handleNavigation(item.path)}
                                >
                                    {item.label}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {/* Language Selector & CTA Button */}
                <div className="hidden md:flex items-center gap-4">
                     {/* CTA Button */}
                    <button 
                        className="bg-black text-white border-none py-3 px-6 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 whitespace-nowrap hover:bg-slate-700 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(30,41,59,0.3)]"
                        onClick={() => handleNavigation('/book-consultation')}
                    >
                        Book a consultation
                    </button>
                    {/* Language Selector */}
                    <div className="relative">
                        <button
                            className="flex items-center gap-2 border border-black text-gray-700 py-2 px-4 rounded-full text-sm font-medium cursor-pointer transition-all duration-300 hover:border-gray-400 hover:bg-gray-50"
                            onClick={toggleLanguageDropdown}
                            onMouseEnter={() => setIsLanguageDropdownOpen(true)}
                        >
                            <span>{currentLanguage}</span>
                            <FaChevronDown className={`text-xs transition-transform duration-300 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <div 
                            className={`absolute top-full right-0 bg-white border border-gray-200 rounded-xl shadow-[0_10px_25px_rgba(0,0,0,0.1)] py-2 min-w-[160px] z-[1001] transition-all duration-300 ${
                                isLanguageDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2.5'
                            }`}
                            onMouseLeave={() => setIsLanguageDropdownOpen(false)}
                        >
                            {languages.map((language) => (
                                <button
                                    key={language.code}
                                    className={`flex items-center gap-3 w-full text-gray-700 border-black text-sm font-medium cursor-pointer py-3 px-4 text-left transition-all duration-300 hover:bg-gray-50 ${
                                        currentLanguage === language.code ? 'bg-blue-50 text-blue-600' : ''
                                    }`}
                                    onClick={() => handleLanguageChange(language.code)}
                                >
                                    <span className="text-lg">{language.flag}</span>
                                    <span>{language.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                   
                </div>

                {/* Mobile Menu Button */}
                <button className="md:hidden bg-none border-none text-xl cursor-pointer p-2 rounded-lg transition-all duration-300 hover:bg-slate-100" onClick={toggleMobileMenu}>
                    {isMobileMenuOpen ? <FaTimes size={15}  /> : <FaBars size={18} />}
                </button>
            </div>

            {/* Mobile Menu */}
            <div className={`md:hidden fixed top-0 left-0 right-0 bottom-0 bg-white h-screen z-[999] transition-all duration-300 ${
                isMobileMenuOpen ? 'translate-x-0 opacity-100 visible' : '-translate-x-full opacity-0 invisible'
            }`}>
                {/* Mobile Menu Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-white">
                    <div className="flex items-center gap-3">
                        <img src="/logowetekup.svg" alt="WeTekup" className="h-40 w-auto -my-20 max-md:-mx-2" />
                        {/* <span className="text-base tracking-wider font-bold text-slate-800"><span className='font-light'>We</span>Tekup</span> */}
                    </div>
                    <button 
                        className="text-slate-800 text-xl p-2 rounded-lg transition-all duration-300 hover:bg-slate-100" 
                        onClick={toggleMobileMenu}
                    >
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Mobile Menu Content */}
                <div className="flex flex-col h-full px-8 py-2 justify-between bg-white">
                    {/* Navigation Items */}
                    <div className="space-y-1 bg-white">
                        {navigationItems.map((item) => (
                            <div key={item.label} className="mb-2">
                                {item.hasDropdown ? (
                                    <div>
                                        <button
                                            className={`flex items-center justify-between w-full text-black text-lg font-light cursor-pointer py-4 text-left transition-all duration-300 hover:text-blue-500 ${
                                                isActivePath(item.path) ? 'text-black font-semibold' : ''
                                            }`}
                                            onClick={toggleAboutDropdown}
                                        >
                                            {item.label}
                                            <FaChevronDown className={`text-sm transition-transform duration-300 ${isAboutDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        <div className={`overflow-hidden transition-all duration-300  bg-white ${isAboutDropdownOpen ? 'max-h-[400px]' : 'max-h-0'}`}>
                                            {item.dropdownItems.map((dropdownItem) => (
                                                <button
                                                    key={dropdownItem.label}
                                                    className="flex items-center justify-start gap-4 w-full text-black text-base font-normal cursor-pointer py-3 text-left transition-all duration-300 hover:text-blue-500"
                                                    onClick={() => handleNavigation(dropdownItem.path)}
                                                >
                                                    <FaArrowRight />
                                                    <span>{dropdownItem.label}</span>
                                                    

                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className={`block w-full text-black text-lg font-light cursor-pointer py-4 text-left transition-all duration-300 hover:text-blue-500 ${
                                            isActivePath(item.path) ? 'text-black font-semibold' : ''
                                        }`}
                                        onClick={() => handleNavigation(item.path)}
                                    >
                                        {item.label}
                                    </button>
                                )}
                            </div>
                        ))}
                        
                     
                    </div>

                    {/* Bottom Buttons */}
                    <div className="space-y-4 pb-8 bg-white relative">
                        <button 
                            className="bg-black text-white border-none py-4 px-8 rounded-full text-base font-semibold cursor-pointer transition-all duration-300 w-full hover:bg-slate-700"
                            onClick={() => handleNavigation('/book-consultation')}
                        >
                            Book a consultation
                        </button>
                        
                        <div className="relative">
                            <button
                                className="flex items-center justify-center gap-2 border border-gray-300 text-black py-4 px-8 rounded-full text-base font-normal cursor-pointer transition-all duration-300 w-full hover:border-gray-400 hover:bg-gray-50"
                                onClick={toggleLanguageDropdown}
                            >
                                <span>{languages.find(lang => lang.code === currentLanguage)?.label || 'English'}</span>
                                <FaChevronDown className={`text-sm transition-transform duration-300 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {/* Language Dropdown for Mobile - appears above the button */}
                            <div className={`absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-[0_10px_25px_rgba(0,0,0,0.1)] py-2 z-[1002] transition-all duration-300 ${
                                isLanguageDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                            }`}>
                                {languages.map((language) => (
                                    <button
                                        key={language.code}
                                        className={`flex items-center gap-3 w-full text-black text-base font-normal cursor-pointer py-3 px-4 text-left transition-all duration-300 hover:bg-gray-50 ${
                                            currentLanguage === language.code ? 'bg-blue-50 text-blue-600 font-semibold' : ''
                                        }`}
                                        onClick={() => handleLanguageChange(language.code)}
                                    >
                                        <span className="text-lg">{language.flag}</span>
                                        <span>{language.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;